/**
 * NWH Celebration Overlay
 * Physics-based confetti explosion from the centre of the screen.
 *
 * Each particle is given a radial velocity vector from the centre point,
 * then updated every frame with gravity + drag. This produces a real
 * "pop outward then fall" motion — not possible with a single CSS keyframe.
 *
 * Usage:
 *   celebration.trigger();
 *   celebration.trigger({ count: 120, emojis: ['🎉','✨'] });
 */

class NWHCelebration {
  constructor(options = {}) {
    this.defaultConfig = {
      emoji: '🎉',
      emojis: ['🎉', '🎊', '✨', '⭐', '🌟', '💥', '🥳'],
      count: 60,          // particles per burst (reduced on mobile)
      gravity: 0.35,      // downward acceleration (px/frame²)
      drag: 0.99,         // horizontal velocity retention per frame
      minVelocity: 10,    // initial speed lower bound (px/frame)
      maxVelocity: 22,    // initial speed upper bound (px/frame)
      upwardBias: 5,      // extra upward kick on spawn (px/frame)
      fadeStart: 0.5,     // life value below which opacity starts dropping
      image: null,        // optional image URL to pop out of centre
      imageAlt: '',       // alt text for the centre image
      imageSize: 300,     // max dimension in px
      imageHold: 2000,    // how long the image stays on screen (ms)
      imagePopIn: 400,    // pop-in duration (ms)
      imageFadeOut: 500,  // fade-out duration (ms)
    };

    this.config = { ...this.defaultConfig, ...options };
    this.overlay = null;
    this.particles = [];
    this.pool = [];
    this.poolSize = 150;
    this.running = false;

    this.init();
  }

  init() {
    const existing = document.getElementById('nwh-celebration-overlay');
    if (existing) {
      this.overlay = existing;
    } else {
      this.overlay = document.createElement('div');
      this.overlay.id = 'nwh-celebration-overlay';
      this.overlay.className = 'nwh-celebration-overlay';
      this.overlay.setAttribute('aria-hidden', 'true');
      document.body.appendChild(this.overlay);
    }

    for (let i = 0; i < this.poolSize; i++) {
      this.pool.push(this.createElement());
    }
  }

  createElement() {
    const el = document.createElement('span');
    el.className = 'nwh-celebration-item';
    el.setAttribute('aria-hidden', 'true');
    return el;
  }

  getElement() {
    return this.pool.pop() || this.createElement();
  }

  releaseElement(el) {
    if (el.parentNode) el.parentNode.removeChild(el);
    el.style.cssText = '';
    if (this.pool.length < this.poolSize) this.pool.push(el);
  }

  pickEmoji(config) {
    if (Array.isArray(config.emojis) && config.emojis.length) {
      return config.emojis[Math.floor(Math.random() * config.emojis.length)];
    }
    return config.emoji;
  }

  /**
   * Spawn a burst of particles from the centre of the viewport.
   */
  trigger(options = {}) {
    const config = { ...this.config, ...options };
    if (!this.overlay) return;

    // Respect reduced motion — just skip it
    if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      console.log('[NWHCelebration] skipped (prefers-reduced-motion)');
      return;
    }

    const centerX = window.innerWidth / 2;
    const centerY = window.innerHeight / 2;
    // Scale particle count down on small screens — fewer layers for the GPU to juggle
    const count = window.innerWidth < 640 ? Math.round(config.count * 0.6) : config.count;

    // Scale the burst size to the viewport so emoji don't fly off-screen on mobile.
    // Tuned for a 1200px reference width; floors at 0.4 so very small screens still get a real burst.
    const viewportScale = Math.min(Math.max(window.innerWidth / 1200, 0.4), 1);
    const minVel = config.minVelocity * viewportScale;
    const maxVel = config.maxVelocity * viewportScale;
    const upBias = config.upwardBias * viewportScale;
    console.log('[NWHCelebration] trigger', { count, centerX, centerY, viewportScale });

    // Batch all new particles into one DocumentFragment — single DOM append
    const frag = document.createDocumentFragment();

    for (let i = 0; i < count; i++) {
      const el = this.getElement();
      el.textContent = this.pickEmoji(config);
      // Defensive: override any stale CSS from a previous animation approach
      el.style.cssText = 'position:absolute;top:0;left:0;opacity:1;animation:none;';
      frag.appendChild(el);

      // Full 360° radial burst with an upward bias applied after.
      const angle = Math.random() * Math.PI * 2;
      const speed = minVel + Math.random() * (maxVel - minVel);

      this.particles.push({
        el,
        x: centerX,
        y: centerY,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed - upBias,
        rotation: Math.random() * 360,
        rotationSpeed: (Math.random() - 0.5) * 20,
        scale: 0.8 + Math.random() * 0.5,
        life: 1,
        fade: 0.005 + Math.random() * 0.004,
        lastOpacity: 1,
      });
    }

    this.overlay.appendChild(frag);

    // Optional centre image — pops out, holds, then fades away.
    if (config.image) {
      this.showCenterImage(config);
    }

    if (!this.running) this.animate();
  }

  /**
   * Display the optional centre image with a pop-in → hold → fade-out sequence.
   * Lives outside the physics loop — purely a timed CSS animation.
   */
  showCenterImage(config) {
    // If a previous image is still on screen, remove it before adding a new one
    const prev = this.overlay.querySelector('.nwh-celebration-image');
    if (prev) prev.remove();

    const img = document.createElement('img');
    img.className = 'nwh-celebration-image';
    img.src = config.image;
    img.alt = config.imageAlt || '';
    img.setAttribute('aria-hidden', 'true');
    img.style.maxWidth = config.imageSize + 'px';
    img.style.maxHeight = config.imageSize + 'px';
    this.overlay.appendChild(img);

    // Force a reflow so the initial state (scale 0) is committed before we transition
    // eslint-disable-next-line no-unused-expressions
    img.offsetWidth;

    // Pop in
    img.style.transition =
      'transform ' + config.imagePopIn + 'ms cubic-bezier(0.34, 1.56, 0.64, 1), opacity ' + config.imagePopIn + 'ms ease-out';
    img.classList.add('is-visible');

    // Hold → fade out → remove
    setTimeout(() => {
      img.style.transition = 'opacity ' + config.imageFadeOut + 'ms ease-in';
      img.classList.add('is-leaving');
      setTimeout(() => img.remove(), config.imageFadeOut + 50);
    }, config.imagePopIn + config.imageHold);
  }

  /**
   * Main animation loop — runs until no particles remain.
   */
  animate() {
    this.running = true;
    const gravity = this.config.gravity;
    const drag = this.config.drag;
    const fadeStart = this.config.fadeStart;

    const tick = () => {
      const alive = [];
      const maxY = window.innerHeight + 100;
      const particles = this.particles;
      const len = particles.length;

      for (let i = 0; i < len; i++) {
        const p = particles[i];
        p.vy += gravity;
        p.vx *= drag;
        p.x += p.vx;
        p.y += p.vy;
        p.rotation += p.rotationSpeed;
        p.life -= p.fade;

        // translate3d forces GPU layer; pure-px transform avoids per-frame % resolution.
        // Element is centred visually via negative margin in CSS, not via translate(-50%,-50%).
        p.el.style.transform =
          'translate3d(' + p.x + 'px,' + p.y + 'px,0) rotate(' + p.rotation + 'deg) scale(' + p.scale + ')';

        // Only write opacity when it actually changes — saves ~40% of style writes
        if (p.life < fadeStart) {
          const op = p.life > 0 ? p.life / fadeStart : 0;
          if (op !== p.lastOpacity) {
            p.el.style.opacity = op;
            p.lastOpacity = op;
          }
        }

        if (p.life > 0 && p.y < maxY) {
          alive.push(p);
        } else {
          this.releaseElement(p.el);
        }
      }

      this.particles = alive;

      if (this.particles.length > 0) {
        requestAnimationFrame(tick);
      } else {
        this.running = false;
        console.log('[NWHCelebration] animation complete');
      }
    };

    requestAnimationFrame(tick);
  }

  destroy() {
    this.particles.forEach((p) => this.releaseElement(p.el));
    this.particles = [];
    if (this.overlay && this.overlay.parentNode) {
      this.overlay.parentNode.removeChild(this.overlay);
    }
    this.pool = [];
    this.running = false;
  }
}

// Global celebration instance - ready to use anywhere
const celebration = new NWHCelebration();
